import $ from 'jquery';

const IMAGE_MERGE_TAG_PATTERN = /^\[\[[a-zA-Z0-9_:-]+\]\]$/;

// Holds the template's own height attribute while a merged image is previewed at its real
// proportions, so the preview never reaches saved html.
const TEMPLATE_HEIGHT_ATTRIBUTE = 'data-template-height';

// Real asset urls to preview for image merge tags, keyed by tag name. Without a preview the
// editor shows a blank box, which hides the fact that the merged file will be stretched into
// whatever slot the template sized for a different image.
export type EmailImageMergeTagPreviews = Record<string, string>;

// Fallback for a merge tag we cannot resolve to a real asset.
export const EMAIL_IMAGE_MERGE_TAG_PLACEHOLDER_SRC =
    'data:image/svg+xml,' + encodeURIComponent(
        '<svg xmlns="http://www.w3.org/2000/svg" width="200" height="120" viewBox="0 0 200 120">' +
        '<rect width="200" height="120" fill="#f5f5f5" stroke="#bdbdbd" stroke-width="1"/>' +
        '</svg>',
    );

export const EMAIL_ORGANIZATION_LOGO_MERGE_TAG = 'organization_logo_url';

export function isImageMergeTag(url: string | null | undefined): boolean {
    const cleaned = (url || '').trim();
    return cleaned.length > 0 && IMAGE_MERGE_TAG_PATTERN.test(cleaned);
}

export function imageMergeTagName(url: string | null | undefined): string {
    if (!isImageMergeTag(url)) {
        return '';
    }

    return (url || '').trim().slice(2, -2);
}

export function displayUrlForEmailImageAssetUrl(assetUrl: string, previews: EmailImageMergeTagPreviews = {}): string {
    if (isImageMergeTag(assetUrl)) {
        return previews[imageMergeTagName(assetUrl)] || EMAIL_IMAGE_MERGE_TAG_PLACEHOLDER_SRC;
    }

    return assetUrl || '';
}

function stashTemplateHeight($img: JQuery): void {
    const height = $img.attr('height');

    if (height) {
        $img.attr(TEMPLATE_HEIGHT_ATTRIBUTE, height);
    }

    $img.removeAttr('height');
}

function restoreTemplateHeight($img: JQuery): void {
    const height = $img.attr(TEMPLATE_HEIGHT_ATTRIBUTE);

    if (height) {
        $img.attr('height', height);
    }

    $img.removeAttr(TEMPLATE_HEIGHT_ATTRIBUTE);
}

export function applyEmailImageAssetToElement($img: JQuery, assetUrl: string, previews: EmailImageMergeTagPreviews = {}): void {
    const trimmed = (assetUrl || '').trim();

    if (!trimmed) {
        $img.removeAttr('data-asset-url');
        $img.removeAttr('src');
        return;
    }

    $img.attr('data-asset-url', trimmed);
    $img.attr('src', displayUrlForEmailImageAssetUrl(trimmed, previews));

    // A merged image is whatever the org uploaded, so the template's height would stretch it.
    // Drop it for the preview only; the attribute goes back before save, and sending strips it
    // again for real.
    if (isImageMergeTag(trimmed)) {
        stashTemplateHeight($img);
        return;
    }

    restoreTemplateHeight($img);
}

export function applyEmailImageEditorPlaceholders(scope?: JQuery | Document | Element, previews: EmailImageMergeTagPreviews = {}): void {
    const $images = scope ? $(scope).find('img') : $('img');

    $images.each(function() {
        const $img = $(this);
        const src = ($img.attr('src') || '').trim();
        const dataAssetUrl = ($img.attr('data-asset-url') || '').trim();
        const mergeTagUrl = isImageMergeTag(dataAssetUrl) ? dataAssetUrl
            : isImageMergeTag(src) ? src
            : '';

        if (!mergeTagUrl) {
            return;
        }

        applyEmailImageAssetToElement($img, mergeTagUrl, previews);
    });
}

export function restoreEmailImageMergeTagSources(scope?: JQuery | Document | Element): void {
    const $images = scope ? $(scope).find('img') : $('img');

    $images.each(function() {
        const $img = $(this);
        const dataAssetUrl = ($img.attr('data-asset-url') || '').trim();

        // Runs for every image so no preview state survives into the saved body
        restoreTemplateHeight($img);

        if (!isImageMergeTag(dataAssetUrl)) {
            return;
        }

        $img.attr('src', dataAssetUrl);
    });
}

export function cleanupEmailAddress(emailAddress: string): string {

	// Trim string
	const cleaned = (emailAddress || "").trim();

	// If we have an empty string, abort
	if (cleaned === "") {
		return "";
	}

	// Validating email address
    // Also allow for merge tags before the @ sign
    // ie [[organization_name]]@gmail.com
	const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|\[\[[a-zA-Z0-9_:-]+\]\])@((\[[0-9]{1,3}(\.[0-9]{1,3}){3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    // Allow for a solo merge tag
    // ie [[customer_email_address]]
    const mergeTagPattern = /^\[\[[a-zA-Z0-9_:-]+\]\]$/;
    if (pattern.test(cleaned) || mergeTagPattern.test(cleaned)) {
		return cleaned.toLowerCase();
	}

	return "";
}

export function hasValidEmail(emailAddress: string): boolean {
    return cleanupEmailAddress(emailAddress)?.length > 0 || false;
}